/**
 * Code-Aware Conversation Service
 * 
 * This service handles communication with the code-aware conversation API
 * for integrating Git repositories with conversation-based code assistance.
 * Includes Claude SDK stream integration for real-time code analysis.
 */

import api from './api';

/**
 * Initialize a code-aware conversation
 * @param {string} projectId - Project ID
 * @param {string} conversationId - Conversation ID
 * @param {string} taskKey - Task key (e.g., 'architecture', 'development')
 * @returns {Promise<Object>} - Code-aware conversation information
 */
export const initializeCodeAwareConversation = async (projectId, conversationId, taskKey) => {
  try {
    const response = await api.post('/code-aware-conversations/initialize', {
      projectId,
      conversationId,
      taskKey
    });
    return response.data;
  } catch (error) {
    console.error('Error initializing code-aware conversation:', error);
    throw error;
  }
};

/**
 * Send a message in a code-aware conversation
 * @param {string} conversationId - Conversation ID
 * @param {string} message - Message text
 * @param {Object} options - Additional options
 * @param {string} options.fileContext - File path for context (optional)
 * @param {Object} options.searchContext - Search results for context (optional)
 * @returns {Promise<Object>} - Response message
 */
export const sendCodeAwareMessage = async (conversationId, message, options = {}) => {
  try {
    const response = await api.post(`/code-aware-conversations/${conversationId}/messages`, {
      message,
      ...options
    });
    return response.data;
  } catch (error) {
    console.error('Error sending code-aware message:', error);
    throw error;
  }
};

/**
 * Search code in a repository
 * @param {string} projectId - Project ID
 * @param {string} query - Search query
 * @param {Array<string>} filePatterns - File patterns to search (optional)
 * @returns {Promise<Object>} - Search results
 */
export const searchCode = async (projectId, query, filePatterns = []) => {
  try {
    const response = await api.post(`/code-aware-conversations/${projectId}/search`, {
      query,
      filePatterns
    });
    return response.data;
  } catch (error) {
    console.error('Error searching code:', error);
    throw error;
  }
};

/**
 * Generate code in a conversation
 * @param {string} conversationId - Conversation ID
 * @param {string} prompt - Code generation prompt
 * @param {string} language - Programming language (optional)
 * @returns {Promise<Object>} - Generated code information
 */
export const generateCodeInConversation = async (conversationId, prompt, language = 'html') => {
  try {
    const response = await api.post(`/code-aware-conversations/${conversationId}/generate-code`, {
      prompt,
      language
    });
    return response.data;
  } catch (error) {
    console.error('Error generating code in conversation:', error);
    throw error;
  }
};

/**
 * Get code-aware conversation status
 * @param {string} conversationId - Conversation ID
 * @returns {Promise<boolean>} - Whether the conversation is code-aware
 */
export const isCodeAwareConversation = async (conversationId) => {
  try {
    // Fetch conversation details using the conversation service
    const conversationService = require('./conversationService');
    const conversation = await conversationService.getConversation(conversationId);
    
    // Check if the conversation has code-aware metadata
    return !!(conversation?.data?.metadata?.codeAware);
  } catch (error) {
    console.error('Error checking code-aware status:', error);
    return false;
  }
};

/**
 * Get all available tasks that can be generated using Claude SDK
 */
export const getAvailableTasks = async () => {
  try {
    const response = await api.get('/code-aware-conversations/tasks');
    return response.data;
  } catch (error) {
    console.error('Error fetching available tasks:', error);
    throw error;
  }
};

/**
 * Validate that a project has a valid workspace with repositories
 */
export const validateProjectWorkspace = async (projectId) => {
  try {
    const response = await api.get(`/code-aware-conversations/${projectId}/validate-workspace`);
    return response.data;
  } catch (error) {
    console.error('Error validating project workspace:', error);
    throw error;
  }
};

/**
 * Generate conversation for a specific task using Claude SDK stream
 * Returns a fetch response for streaming
 */
export const generateTaskConversation = async (projectId, taskId, options = {}) => {
  const { systemPrompt, customPrompt } = options;
  
  // Prepare the request body
  const requestBody = {};
  if (systemPrompt) requestBody.systemPrompt = systemPrompt;
  if (customPrompt) requestBody.customPrompt = customPrompt;

  // Create fetch request for streaming
  const token = localStorage.getItem('token');
  const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const url = `${baseURL}/code-aware-conversations/${projectId}/tasks/${taskId}/generate`;
  
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Accept': 'text/event-stream',
      'Cache-Control': 'no-cache'
    },
    body: JSON.stringify(requestBody)
  });
};

/**
 * Execute a custom Claude SDK command
 * Returns a fetch response for streaming
 */
export const executeCustomCommand = async (projectId, options = {}) => {
  const { 
    prompt, 
    systemPrompt, 
    allowedTools, 
    appendSystemPrompt, 
    maxTurns = 10 
  } = options;

  if (!prompt) {
    throw new Error('Prompt is required for custom command execution');
  }

  // Prepare the request body
  const requestBody = { prompt, maxTurns };
  if (systemPrompt) requestBody.systemPrompt = systemPrompt;
  if (allowedTools) requestBody.allowedTools = allowedTools;
  if (appendSystemPrompt) requestBody.appendSystemPrompt = appendSystemPrompt;

  // Create fetch request for streaming
  const token = localStorage.getItem('token');
  const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const url = `${baseURL}/code-aware-conversations/${projectId}/execute`;
  
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Accept': 'text/event-stream',
      'Cache-Control': 'no-cache'
    },
    body: JSON.stringify(requestBody)
  });
};

/**
 * Process streaming response from Claude SDK
 * Returns a promise that resolves with all messages
 */
export const processStreamResponse = async (response, onMessage = null, onError = null) => {
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const reader = response.body?.getReader();
  const decoder = new TextDecoder();
  const messages = [];

  if (!reader) {
    throw new Error('Unable to read response stream');
  }

  try {
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        break;
      }

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6).trim();
          
          if (data === '[DONE]') {
            return messages;
          }

          try {
            const message = JSON.parse(data);
            messages.push(message);

            // Call onMessage callback if provided
            if (onMessage && typeof onMessage === 'function') {
              onMessage(message);
            }

            // Handle error messages
            if (message.type === 'result' && message.is_error) {
              const error = new Error(message.error || 'Unknown error occurred');
              if (onError && typeof onError === 'function') {
                onError(error, message);
              } else {
                console.error('Stream error:', error);
              }
            }
          } catch (parseError) {
            console.warn('Failed to parse message:', data, parseError);
          }
        }
      }
    }

    return messages;
  } catch (error) {
    console.error('Error processing stream:', error);
    if (onError && typeof onError === 'function') {
      onError(error);
    }
    throw error;
  } finally {
    reader.releaseLock();
  }
};

/**
 * Helper method to generate conversation with callbacks
 */
export const generateTaskConversationWithCallbacks = async (projectId, taskId, options = {}) => {
  const { systemPrompt, customPrompt, onMessage, onError, onComplete } = options;

  try {
    const response = await generateTaskConversation(projectId, taskId, {
      systemPrompt,
      customPrompt
    });

    const messages = await processStreamResponse(
      response,
      onMessage,
      onError
    );

    if (onComplete && typeof onComplete === 'function') {
      onComplete(messages);
    }

    return messages;
  } catch (error) {
    if (onError && typeof onError === 'function') {
      onError(error);
    }
    throw error;
  }
};

/**
 * Helper method to execute custom command with callbacks
 */
export const executeCustomCommandWithCallbacks = async (projectId, options = {}) => {
  const { 
    prompt, 
    systemPrompt, 
    allowedTools, 
    appendSystemPrompt, 
    maxTurns,
    onMessage, 
    onError, 
    onComplete 
  } = options;

  try {
    const response = await executeCustomCommand(projectId, {
      prompt,
      systemPrompt,
      allowedTools,
      appendSystemPrompt,
      maxTurns
    });

    const messages = await processStreamResponse(
      response,
      onMessage,
      onError
    );

    if (onComplete && typeof onComplete === 'function') {
      onComplete(messages);
    }

    return messages;
  } catch (error) {
    if (onError && typeof onError === 'function') {
      onError(error);
    }
    throw error;
  }
};

// Export all functions as default object
export default {
  initializeCodeAwareConversation,
  sendCodeAwareMessage,
  searchCode,
  generateCodeInConversation,
  isCodeAwareConversation,
  getAvailableTasks,
  validateProjectWorkspace,
  generateTaskConversation,
  executeCustomCommand,
  processStreamResponse,
  generateTaskConversationWithCallbacks,
  executeCustomCommandWithCallbacks
};